import React from 'react';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import EditableObjectHandle from '../Common/EditableObjectHandler';

// Wrapper for EditableObjectHandle positioning
const HeaderWrapper = styled.div`
  position: relative;
`;

class HeaderType01 extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    const intl = this.props.intl;

    // SNS 링크 색상 - styled component에서 사용하므로 먼저 정의
    const snsLinkBackgroundColor = this.props.snsLinkBackgroundColor || '#ffffff';
    const snsLinkTextColor = this.props.snsLinkTextColor || '#6f7075';

    const TitleContainer = styled.section`
      position: relative;
      background-color: ${this.props.backgroundColor || 'transparent'};
      & .title-logo {
        height: 75px;
      }
    `;
    const Title = styled.h1`
      font-size: 50px;
      font-weight: 900;
      line-height: 1.18;
      color: #2e2f36;
      margin: 0 0 5px 0;
      line-height: 1;

      a {
        color: inherit;
        text-decoration: none;
        transition: opacity 0.2s ease;

        &:hover {
          opacity: 0.8;
        }
      }
    `;

    const Subtitle = styled.h5`
      color: #888;
      font-weight: 400;
      line-height: 1.47;
      letter-spacing: -1.3px;
      font-size: 19px;
    `;

    const TitleImageWrapper = styled.div`
      margin-bottom: 10px;

      a {
        display: inline-block;
        transition: opacity 0.2s ease;

        &:hover {
          opacity: 0.8;
        }
      }

      img {
        max-height: 120px;
        width: auto;
        object-fit: contain;

        @media (max-width: 768px) {
          max-height: 80px;
        }
      }
    `;

    const SNSLinks = styled.div`
      position: absolute;
      right: -12px;
      bottom: -6px;
      border-radius: 6px 0 0 6px;
      overflow: hidden;

      & a {
        color: ${snsLinkTextColor};
      }

      & .sns-container {
        width: 160px;
        transition: all 0.3s;
        cursor: pointer;
        align-items: center;
        font-size: 14px;
        background-color: ${snsLinkBackgroundColor};
      }

      & .sns-container:hover {
        filter: brightness(0.9);
      }

      & .sns-name {
        width: 300px;
        text-align: left;
        font-weight: 400;
      }

      & .sns-button {
        margin-left: auto;
        padding-right: 12px !important;
        width: 60px;
      }

      @media (max-width: 752px) {
        & .sns-container {
          width: 40px;
          transition: all 0.3s;
          cursor: pointer;
        }
      }
    `;
    const sns = this.props.sns;
    const blogEnabled = this.props.blogEnabled;
    const titleMode = this.props.titleMode || 'text';
    const titleImage = this.props.titleImage || '';
    const siteId = this.props.siteId;

    // 회사 로고 관련 props
    const companyLogo = this.props.companyLogo || '';
    const companyLogoLink = this.props.companyLogoLink || '';
    const companyLogoPosition = this.props.companyLogoPosition || 'top';
    const companyLogoMargin = this.props.companyLogoMargin || '0 0 15px 0';
    const companyLogoWidth = this.props.companyLogoWidth || 'auto';
    const companyLogoHeight = this.props.companyLogoHeight || '50px';

    // titleImage 스타일 옵션
    const titleImageMargin = this.props.titleImageMargin || '0 0 10px 0';
    const titleImageMaxHeight = this.props.titleImageMaxHeight || '120px';
    const titleImageMaxWidth = this.props.titleImageMaxWidth || 'auto';

    // 헤더 배경색
    const backgroundColor = this.props.backgroundColor || 'transparent';

    // Objects for EditableObjectHandle
    const headerObjects = {
      titleMode,
      titleImage,
      // 회사 로고 관련
      companyLogo,
      companyLogoLink,
      companyLogoPosition,
      companyLogoMargin,
      companyLogoWidth,
      companyLogoHeight,
      // titleImage 스타일 옵션
      titleImageMargin,
      titleImageMaxHeight,
      titleImageMaxWidth,
      // 헤더 배경색
      backgroundColor,
      // SNS 링크 색상
      snsLinkBackgroundColor,
      snsLinkTextColor,
    };

    const defaultObjects = {
      titleMode: 'text',
      titleImage: '',
      companyLogo: '',
      companyLogoLink: '',
      companyLogoPosition: 'top',
      companyLogoMargin: '0 0 15px 0',
      companyLogoWidth: 'auto',
      companyLogoHeight: '50px',
      titleImageMargin: '0 0 10px 0',
      titleImageMaxHeight: '120px',
      titleImageMaxWidth: 'auto',
      backgroundColor: 'transparent',
      snsLinkBackgroundColor: '#ffffff',
      snsLinkTextColor: '#6f7075',
    };

    // 회사 로고 스타일 컴포넌트
    const CompanyLogoWrapper = styled.div`
      margin: ${companyLogoMargin};

      img {
        width: ${companyLogoWidth};
        height: ${companyLogoHeight};
        object-fit: contain;
      }
    `;

    // 수정된 TitleImageWrapper (동적 스타일 적용)
    const DynamicTitleImageWrapper = styled.div`
      margin: ${titleImageMargin};

      a {
        display: inline-block;
        transition: opacity 0.2s ease;

        &:hover {
          opacity: 0.8;
        }
      }

      img {
        max-height: ${titleImageMaxHeight};
        max-width: ${titleImageMaxWidth};
        width: auto;
        object-fit: contain;

        @media (max-width: 768px) {
          max-height: 80px;
        }
      }
    `;

    // 왼쪽 정렬용 Flex 컨테이너
    const LeftAlignedContainer = styled.div`
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
      }
    `;

    // 회사 로고 렌더링 함수 (에이전시 사이트 외부 링크)
    const renderCompanyLogo = () => {
      if (!companyLogo) return null;

      // 외부 링크가 있으면 <a> 태그 사용, 없으면 이미지만 표시
      if (companyLogoLink) {
        return (
          <CompanyLogoWrapper>
            <a href={companyLogoLink} target="_blank" rel="noopener noreferrer">
              <img src={companyLogo} alt="Company Logo" />
            </a>
          </CompanyLogoWrapper>
        );
      }

      return (
        <CompanyLogoWrapper>
          <img src={companyLogo} alt="Company Logo" />
        </CompanyLogoWrapper>
      );
    };

    // 타이틀 콘텐츠 렌더링 함수
    const renderTitleContent = () => {
      if (titleMode === 'image' && titleImage) {
        return (
          <DynamicTitleImageWrapper>
            <Link to="/">
              <img src={titleImage} alt={this.props.title || 'Site Title'} />
            </Link>
          </DynamicTitleImageWrapper>
        );
      }
      return (
        <>
          <Title className="fw-bold mb-2">
            <Link to="/">{Parser(this.props.title)}</Link>
          </Title>
          <Subtitle>{Parser(this.props.subtitle)}</Subtitle>
        </>
      );
    };

    // 위치에 따른 콘텐츠 렌더링
    const renderHeaderContent = () => {
      if (companyLogoPosition === 'left' && companyLogo) {
        return (
          <LeftAlignedContainer>
            {renderCompanyLogo()}
            <div>{renderTitleContent()}</div>
          </LeftAlignedContainer>
        );
      }

      if (companyLogoPosition === 'top') {
        return (
          <>
            {renderCompanyLogo()}
            {renderTitleContent()}
          </>
        );
      }

      if (companyLogoPosition === 'bottom') {
        return (
          <>
            {renderTitleContent()}
            {renderCompanyLogo()}
          </>
        );
      }

      // 기본값 (로고 없을 때)
      return renderTitleContent();
    };

    return (
      <HeaderWrapper>
        <EditableObjectHandle
          id={siteId}
          objects={headerObjects}
          defaultObjects={defaultObjects}
          componentTitle="HeaderType01"
        />
        <TitleContainer className={this.props.containerClass}>
        {this.props.logo && (
          <img
            src={this.props.logo}
            className="title-logo mb-5"
            alt="title-logo"
          />
        )}

        {/* 헤더 콘텐츠 (회사 로고 + 타이틀) 렌더링 */}
        {renderHeaderContent()}
        <SNSLinks>
          {blogEnabled && (
             <Link to="/blog">
              <div className="d-flex bd-highlight sns-container">
                <div className="p-2 bd-highlight">
                  <img
                    src="https://img.icons8.com/ios/20/000000/blogger.png"
                    alt="btn-blog"
                  />
                </div>
                <div className="p-2 bd-highlight d-none d-sm-none d-md-block sns-name">
                  Blog
                </div>
                <div className="p-2 bd-highlight sns-button">
                  <img
                    className="d-none d-sm-none d-md-block"
                    src="https://img.icons8.com/ios-filled/15/B0B1B7/circled-chevron-right.png"
                    alt="circled-chevron-right"
                  />
                </div>
              </div>
            </Link>
          )}
          {sns.facebook && (
            <a href={sns.facebook} target="_blank" rel="noreferrer">
              <div className="d-flex bd-highlight sns-container">
                <div className="p-2 bd-highlight">
                  <img
                    src="https://img.icons8.com/color/20/000000/facebook.png"
                    alt="btn-facebook"
                  />
                </div>
                <div className="p-2 bd-highlight d-none d-sm-none d-md-block sns-name">
                  {intl.formatMessage({ id: 'common.facebook' })}
                </div>
                <div className="p-2 bd-highlight sns-button">
                  <img
                    className="d-none d-sm-none d-md-block"
                    src="https://img.icons8.com/ios-filled/15/B0B1B7/circled-chevron-right.png"
                    alt="circled-chevron-right"
                  />
                </div>
              </div>
            </a>
          )}
          {sns.instagram && (
            <a href={sns.instagram} target="_blank" rel="noreferrer">
              <div className="d-flex bd-highlight sns-container">
                <div className="p-2 bd-highlight">
                  <img
                    src="https://img.icons8.com/windows/20/DA525D/instagram-new.png"
                    alt="btn-instagram"
                  />
                </div>
                <div className="p-2 bd-highlight d-none d-sm-none d-md-block sns-name">
                  {intl.formatMessage({ id: 'common.instagram' })}
                </div>
                <div className="p-2 bd-highlight sns-button">
                  <img
                    className="d-none d-sm-none d-md-block"
                    src="https://img.icons8.com/ios-filled/15/B0B1B7/circled-chevron-right.png"
                    alt="circled-chevron-right"
                  />
                </div>
              </div>
            </a>
          )}
          {sns.youtube && (
            <a href={sns.youtube} target="_blank" rel="noreferrer">
              <div className="d-flex bd-highlight sns-container">
                <div className="p-2 bd-highlight">
                  <img
                    src="https://img.icons8.com/color/20/000000/youtube-play.png"
                    alt="btn-youtube"
                  />
                </div>
                <div className="p-2 bd-highlight d-none d-sm-none d-md-block sns-name">
                  {intl.formatMessage({ id: 'common.youtube' })}
                </div>
                <div className="p-2 bd-highlight sns-button">
                  <img
                    className="d-none d-sm-none d-md-block"
                    src="https://img.icons8.com/ios-filled/15/B0B1B7/circled-chevron-right.png"
                    alt="circled-chevron-right"
                  />
                </div>
              </div>
            </a>
          )}
        </SNSLinks>
      </TitleContainer>
      </HeaderWrapper>
    );
  }
}

HeaderType01.propTypes = {
  containerClass: PropTypes.string,
  logo: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  blogEnabled: PropTypes.bool,
  sns: PropTypes.shape({
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    youtube: PropTypes.string,
  }),
  titleMode: PropTypes.oneOf(['text', 'image']),
  titleImage: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // 회사 로고 관련
  companyLogo: PropTypes.string,
  companyLogoLink: PropTypes.string,
  companyLogoPosition: PropTypes.oneOf(['top', 'left', 'bottom']),
  companyLogoMargin: PropTypes.string,
  companyLogoWidth: PropTypes.string,
  companyLogoHeight: PropTypes.string,
  // titleImage 스타일 옵션
  titleImageMargin: PropTypes.string,
  titleImageMaxHeight: PropTypes.string,
  titleImageMaxWidth: PropTypes.string,
  // 헤더 배경색
  backgroundColor: PropTypes.string,
  // SNS 링크 색상
  snsLinkBackgroundColor: PropTypes.string,
  snsLinkTextColor: PropTypes.string,
};

HeaderType01.defaultProps = {
  containerClass: 'my-4 text-center',
  logo: '',
  title: '',
  subtitle: '',
  blogEnabled: false,
  sns: {
    facebook: '',
    instagram: '',
    youtube: '',
  },
  titleMode: 'text',
  titleImage: '',
  siteId: null,
  // 회사 로고 관련
  companyLogo: '',
  companyLogoLink: '',
  companyLogoPosition: 'top',
  companyLogoMargin: '0 0 15px 0',
  companyLogoWidth: 'auto',
  companyLogoHeight: '50px',
  // titleImage 스타일 옵션
  titleImageMargin: '0 0 10px 0',
  titleImageMaxHeight: '120px',
  titleImageMaxWidth: 'auto',
  // 헤더 배경색
  backgroundColor: 'transparent',
  // SNS 링크 색상
  snsLinkBackgroundColor: '#ffffff',
  snsLinkTextColor: '#6f7075',
};

export default injectIntl(HeaderType01);
